import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BorderOutlined,
  CopyOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Button, Empty, message, Spin, Tooltip } from "antd";
import { io } from "socket.io-client";

// import utils
import isEmpty from "../../validations/is-empty";
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import ChatItem from "./ChatItem";
import PrivateChatItem from "./PrivateChatItem";

// import style
import "../../style/interview/interview.scss";
import MicroPhone from "./MicroPhone";

const allStatus = [
  {
    color: "gold",
    status: "Waiting",
  },
  {
    color: "",
    status: "Recruiter Phone",
  },
  {
    color: "",
    status: "Recruiter Video",
  },
  {
    color: "rgb(190, 0, 161)",
    status: "HR Call",
  },
  {
    color: "rgb(204, 0, 0)",
    status: "Technical",
  },
  {
    color: "rgb(0, 194, 204)",
    status: "Behavioural",
  },
  {
    color: "rgb(0, 157, 16)",
    status: "Final",
  },
  {
    color: "red",
    status: "Failed",
  },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL;

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const generateRandomColor = () => {
  const randomColor = Math.floor(Math.random() * 16777215).toString(16);
  return randomColor;
};

const InterviewPage = ({ title }) => {
  // set title
  document.title = title;

  // get param
  const { id } = useParams();

  const user = useSelector((state) => state.auth.user);
  const timezoneMode = useSelector((state) => state.timezoneMode);
  const socket = useRef();
  const scrollRef = useRef();
  const chatContainerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const scrollPrivateRef = useRef();
  const privateChatContainerRef = useRef(null);
  const [isPrivateAtBottom, setIsPrivateAtBottom] = useState(true);
  const [loading, setLoading] = useState(false);
  const [bidDetail, setBidDetail] = useState(null);
  const [callAppStatus, setCallAppStatus] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [privateConversations, setPrivateConversations] = useState([]);
  const [clientAvatarColor, setClientAvatarColor] = useState("");
  const [transitionText, setTransitionText] = useState("");
  const [tempTransitionText, setTempTransitionText] = useState("");
  const [oldTransitionText, setOldTransitionText] = useState("");
  const [handlePrivateConversations, setHandlePrivateConversations] = useState(
    {},
  );

  useEffect(() => {
    if (!isEmpty(bidDetail)) {
      socket.current = io(SOCKET_BASE_URL);

      socket.current.on("interview_id", (res) => {
        const isValueInArray = res.includes(id);
        setCallAppStatus(isValueInArray);

        if (isValueInArray) {
          socket.current.emit("receiver_interview_id", {
            interview_id: id,
            pdfUrl: `${API_BASE_URL}/job/description/${bidDetail?.pdf_url}`,
            jobDesc: bidDetail?.job_desc,
          });
        }
      });

      socket.current.on("client-message", (res) => {
        setConversations((prev) => [...prev, res]);
        setHandlePrivateConversations({ res, flag: false });
      });

      socket.current.on("message", (res) => {
        setConversations((prev) => [...prev, res]);
      });
    }
  }, [bidDetail, id]);

  const getDifference = (old, current) => {
    const str1 = old;
    const str2 = current;

    if (str2.startsWith(str1)) {
      const difference = str2.slice(str1.length);
      return difference;
    } else {
      return str2;
    }
  };

  useEffect(() => {
    const clientMsg = handlePrivateConversations.res;

    const myVoice = {
      flag: handlePrivateConversations.flag,
      message: getDifference(oldTransitionText, tempTransitionText),
    };

    setOldTransitionText(tempTransitionText);

    if (getDifference(oldTransitionText, tempTransitionText) === "") {
      setPrivateConversations((prev) => [...prev, clientMsg]);
    } else {
      setPrivateConversations((prev) => [...prev, myVoice, clientMsg]);
      socket.current.emit("receive-mic-message", {
        interview_id: id,
        message: getDifference(oldTransitionText, tempTransitionText),
      });
    }
  }, [handlePrivateConversations]);

  useEffect(() => {
    setTempTransitionText(transitionText);
  }, [transitionText]);

  const getColorFromStatus = (status) => {
    const found = allStatus.find((item) => item.status === status);
    return found ? found.color : null;
  };

  const getBidDetail = useCallback(async () => {
    const params = {
      id,
    };

    setLoading(true);

    await apiClient
      .post("/bids/get-detail", params)
      .then((res) => {
        if (res.data.msg !== "success") {
          setBidDetail(null);
          setConversations([]);
          setLoading(false);
        } else {
          setConversations(res.data.interviews);
          setBidDetail(res.data.result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });

    await apiClient
      .post("/bids/get-voice-detail", params)
      .then((res) => {
        if (res.data.msg !== "success") {
          setPrivateConversations([]);
          setLoading(false);
        } else {
          setPrivateConversations(res.data.interviews);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    getBidDetail();
    setClientAvatarColor(generateRandomColor());
  }, [user, getBidDetail]);

  const handlePrivateScroll = () => {
    if (privateChatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        privateChatContainerRef.current;
      const threshold = 20;
      const atBottom = scrollHeight - (scrollTop + clientHeight) < threshold;
      setIsPrivateAtBottom(atBottom);
    }
  };

  useEffect(() => {
    if (isPrivateAtBottom && scrollPrivateRef.current) {
      scrollPrivateRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversations, isPrivateAtBottom, transitionText]);

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        chatContainerRef.current;
      const threshold = 20;
      const atBottom = scrollHeight - (scrollTop + clientHeight) < threshold;
      setIsAtBottom(atBottom);
    }
  };

  useEffect(() => {
    if (isAtBottom && scrollRef.current) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversations, isAtBottom]);

  const handleDownloadResume = (docx_url) => {
    const link = document.createElement("a");
    link.href = `${API_BASE_URL}/job/description/${docx_url}`;
    link.setAttribute("download", "resume.docx");
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownloadJD = async (content, companyname) => {
    const params = {
      content,
    };

    fetch(API_BASE_URL + "/api/bids/download/jd", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${companyname}.txt`; // desired filename
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        message.success("Copied to clipboard!");
      } else {
        console.warn("Fallback: Copy command was unsuccessful");
        message.error("Failed to copy!");
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
      message.error("Failed to copy!");
    }

    document.body.removeChild(textArea);
  };

  const handleCopy = (copyText) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          message.success("Copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy!");
        });
    } else {
      fallbackCopyTextToClipboard(copyText);
    }
  };

  const getFormattedDate = (startDate, endDate) => {
    const start = dayjs(startDate).tz(timezoneMode.timezoneMode);
    const end = dayjs(endDate).tz(timezoneMode.timezoneMode);

    // Get the day of the week, month, and date for the start date
    const weekday = start.format("dddd");
    const monthDay = start.format("MMMM D");

    // Get the time in 12-hour format with AM/PM
    const startTime = start.format("hh:mm A");
    const endTime = end.format("hh:mm A");

    return `${weekday}, ${monthDay} ⋅ ${startTime} - ${endTime}`;
  };

  return (
    <div className="interviewSection">
      <div className="interview-section-title">
        <h2 className="title">
          <PlayCircleOutlined />
          &nbsp; AI Interview
        </h2>
        <div
          className={`call-app-status ${
            callAppStatus ? "green-success" : "yellow-waiting"
          }`}
        >
          {callAppStatus ? "Connected!" : "Waiting..."}
        </div>
      </div>
      <div className="interview-sub-section">
        <div className="scheduled-preview-section">
          {isEmpty(bidDetail) ? (
            <></>
          ) : (
            <div className="scheduled-section">
              <div className="scheduled-title">
                <div
                  className="title-circle"
                  style={{
                    backgroundColor: bidDetail?.caller?.background_color,
                  }}
                ></div>
                <div className="interview-title">
                  {bidDetail.company_name} -{" "}
                  <span
                    style={{
                      fontWeight: "bold",
                      color: `${getColorFromStatus(
                        bidDetail?.status === 0
                          ? "Waiting"
                          : bidDetail?.status === 1
                            ? "Recruiter Phone"
                            : bidDetail?.status === 2
                              ? "Recruiter Video"
                              : bidDetail?.status === 7
                                ? "HR Call"
                                : bidDetail?.status === 3
                                  ? "Technical"
                                  : bidDetail?.status === 4
                                    ? "Behavioural"
                                    : bidDetail?.status === 5
                                      ? "Final"
                                      : "Failed",
                      )}`,
                    }}
                  >
                    {bidDetail?.status === 0
                      ? "Waiting"
                      : bidDetail?.status === 1
                        ? "Recruiter Phone"
                        : bidDetail?.status === 2
                          ? "Recruiter Video"
                          : bidDetail?.status === 7
                            ? "HR Call"
                            : bidDetail?.status === 3
                              ? "Technical"
                              : bidDetail?.status === 4
                                ? "Behavioural"
                                : bidDetail?.status === 5
                                  ? "Final"
                                  : "Failed"}
                  </span>{" "}
                  ({bidDetail?.caller.name})
                </div>
                <div className="schedule-date">
                  {getFormattedDate(bidDetail?.start_date, bidDetail?.end_date)}
                </div>
              </div>
              <div className="meeting-link">
                <div className="meeting-icon">
                  <EnvironmentOutlined />
                </div>
                <Tooltip placement="top" title={bidDetail?.meeting_link}>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => handleCopy(bidDetail?.meeting_link)}
                  >
                    Join with Meeting
                  </Button>
                </Tooltip>
              </div>
              <div className="interviewers-section">
                <div className="interviewers-icon">
                  <UsergroupAddOutlined />
                </div>
                <div className="interviewers">
                  <img
                    alt="caller avatar"
                    className="interview-avatar"
                    src={bidDetail?.caller.avatar}
                  />
                  <p className="interviewer-address">
                    {bidDetail?.caller.email}
                  </p>
                </div>
                <div className="interviewers">
                  <img
                    alt="client avatar"
                    className="interview-avatar"
                    src={`https://ui-avatars.com/api/?name=${bidDetail?.client_address}&background=${clientAvatarColor}&color=fff`}
                  />
                  <p className="interviewer-address">
                    {bidDetail?.client_address}
                  </p>
                </div>
              </div>
              <div className="schedule-main-section">
                <div className="schedule-main-icon">
                  <BorderOutlined />
                </div>

                <Tooltip placement="top" title="Download Resume as PDF">
                  {!isEmpty(bidDetail?.pdf_filename) ? (
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        const url = `${API_BASE_URL}/job/description/${bidDetail?.pdf_url}`;
                        window.open(url, "_blank");
                      }}
                    >
                      {bidDetail?.user.name}.pdf
                    </Button>
                  ) : (
                    <Button
                      disabled
                      icon={<DownloadOutlined />}
                      color="danger"
                      variant="dashed"
                    >
                      {bidDetail?.company_name}
                    </Button>
                  )}
                </Tooltip>

                <Tooltip placement="top" title="Download Resume as Docx">
                  {!isEmpty(bidDetail?.docx_filename) ? (
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadResume(bidDetail?.docx_url)}
                    >
                      {bidDetail?.user.name}.docx
                    </Button>
                  ) : (
                    <Button disabled color="danger" variant="dashed">
                      {bidDetail?.company_name}
                    </Button>
                  )}
                </Tooltip>

                <Tooltip placement="top" title="Download JD">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() =>
                      handleDownloadJD(
                        bidDetail?.job_desc,
                        bidDetail?.company_name,
                      )
                    }
                  >
                    {bidDetail?.company_name} (JD)
                  </Button>
                </Tooltip>
                <Tooltip placement="top" title={bidDetail?.job_url}>
                  <Button
                    type="primary"
                    icon={<CopyOutlined />}
                    onClick={() => handleCopy(bidDetail?.job_url)}
                  >
                    Job URL
                  </Button>
                </Tooltip>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Bidder:{" "}
                  <img
                    alt="bidder avatar"
                    style={{
                      width: "26px",
                      height: "26px",
                      borderRadius: "50%",
                      marginRight: 5,
                      marginLeft: 5,
                    }}
                    src={bidDetail?.user.avatar}
                  />
                  <span style={{ color: bidDetail?.user.background_color }}>
                    {bidDetail?.user.email}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className="interview-chat-section"
          ref={chatContainerRef}
          onScroll={handleScroll}
        >
          {isEmpty(conversations) ? (
            <div className="empty-chat-section">
              <Empty />
            </div>
          ) : (
            conversations.map((item, key) => {
              return (
                <ChatItem
                  key={key}
                  scrollRef={scrollRef}
                  flag={item.flag}
                  desc={item.message}
                />
              );
            })
          )}
        </div>

        <div
          className="last-section"
          ref={privateChatContainerRef}
          onScroll={handlePrivateScroll}
        >
          {isEmpty(bidDetail) ? (
            <div className="empty-chat-section">
              <Empty />
            </div>
          ) : isEmpty(privateConversations) ? (
            <div className="empty-chat-section">
              <Empty />
            </div>
          ) : (
            <>
              {privateConversations.map((item, key) => {
                return (
                  <PrivateChatItem
                    key={key}
                    flag={item?.flag}
                    valid_flag={item?.valid_flag}
                    desc={item?.message}
                    caller_avatar={bidDetail?.caller.avatar}
                    client_address={bidDetail?.client_address}
                    client_avatar_color={clientAvatarColor}
                    scrollRef={scrollPrivateRef}
                  />
                );
              })}

              <MicroPhone
                caller_avatar={bidDetail?.caller.avatar}
                scrollRef={scrollPrivateRef}
                setTransitionText={setTransitionText}
                oldTransitionText={oldTransitionText}
              />
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="interview-loading">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default InterviewPage;
